import { useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { type OrganizationRole } from "@bookdental/schemas";
import { useConfirm } from "@/lib/confirm";
import { trpc } from "@/lib/trpc";
import { useCustomizationLocked } from "@/lib/use-customization-locked";
import { ROLE_LABEL } from "@/components/team/role-meta";
import { CustomRoleCard } from "./CustomRoleCard";

const BASE_ROLE_OPTIONS: Exclude<OrganizationRole, "OWNER" | "PARTNER">[] = [
  "VIEWER",
  "RECEPTIONIST",
  "ASSISTANT",
  "DENTIST",
  "ADMIN",
];

export function CustomRolesSection() {
  const utils = trpc.useUtils();
  const confirm = useConfirm();
  const locked = useCustomizationLocked();
  const rolesQuery = trpc.organization.customRoles.list.useQuery();
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState("");
  const [basedOnRole, setBasedOnRole] = useState<Exclude<OrganizationRole, "OWNER" | "PARTNER">>("ASSISTANT");

  const invalidate = () => {
    utils.organization.customRoles.list.invalidate();
    utils.team.list.invalidate();
  };

  const create = trpc.organization.customRoles.create.useMutation({
    onSuccess: () => {
      invalidate();
      toast.success("Papel customizado criado.");
      setCreating(false);
      setLabel("");
    },
    onError: (err) => toast.error(err.message || "Erro ao criar papel customizado."),
  });

  const remove = trpc.organization.customRoles.delete.useMutation({
    onSuccess: () => {
      invalidate();
      toast.success("Papel customizado excluído.");
    },
    onError: (err) => toast.error(err.message || "Erro ao excluir papel customizado."),
  });

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim()) return;
    create.mutate({ label: label.trim(), basedOnRole });
  };

  const handleDelete = async (id: string, roleLabel: string, memberCount: number) => {
    const ok = await confirm({
      title: `Excluir "${roleLabel}"?`,
      description:
        memberCount > 0
          ? `${memberCount} membro(s) com esse papel voltam automaticamente pro papel-base. Essa ação não pode ser desfeita.`
          : "Essa ação não pode ser desfeita.",
      confirmText: "Excluir",
      destructive: true,
    });
    if (ok) remove.mutate({ id });
  };

  const roles = rolesQuery.data ?? [];

  return (
    <div className="space-y-4 border-t border-zinc-100 pt-5 dark:border-border">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">
            Papéis customizados
          </h3>
          <p className="mt-1 text-[12.5px] text-zinc-500 dark:text-muted-foreground">
            Clone um papel padrão com nome e permissões próprias — útil pra fracionar o mesmo
            cargo (ex: turnos ou funções específicas). O clone fica independente do papel-base
            depois de criado.
          </p>
        </div>
        {!creating && !locked && (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-zinc-200 px-3 text-[12.5px] font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted"
          >
            <Plus className="h-3.5 w-3.5" />
            Criar papel
          </button>
        )}
      </div>

      {creating && (
        <form
          onSubmit={handleCreate}
          className="flex flex-wrap items-end gap-2.5 rounded-lg border border-zinc-200 bg-zinc-50/50 p-3 dark:border-border dark:bg-background/40"
        >
          <div className="min-w-[180px] flex-1">
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
              Nome
            </label>
            <input
              autoFocus
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Ex: Recepção Manhã"
              className="h-9 w-full rounded-md border border-zinc-200 bg-white px-3 text-[13px] text-zinc-900 dark:border-border dark:bg-card dark:text-foreground"
            />
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
              Baseado em
            </label>
            <select
              value={basedOnRole}
              onChange={(e) => setBasedOnRole(e.target.value as Exclude<OrganizationRole, "OWNER" | "PARTNER">)}
              className="h-9 rounded-md border border-zinc-200 bg-white px-2 text-[13px] text-zinc-900 dark:border-border dark:bg-card dark:text-foreground"
            >
              {BASE_ROLE_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r]}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="submit"
              disabled={create.isPending || !label.trim()}
              className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-3 text-[12.5px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-50"
            >
              {create.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Criar
            </button>
            <button
              type="button"
              onClick={() => {
                setCreating(false);
                setLabel("");
              }}
              className="inline-flex h-9 items-center rounded-md border border-zinc-200 px-3 text-[12.5px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted"
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      {rolesQuery.isLoading ? (
        <p className="text-sm text-zinc-500 dark:text-muted-foreground">Carregando...</p>
      ) : roles.length === 0 ? (
        !creating && (
          <p className="text-[12.5px] text-zinc-500 dark:text-muted-foreground">
            Nenhum papel customizado criado ainda.
          </p>
        )
      ) : (
        <div className="space-y-3">
          {roles.map((r: any) => (
            <CustomRoleCard
              key={r.id}
              role={r}
              locked={locked}
              onDelete={() => handleDelete(r.id, r.label, r.memberCount)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
